import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LogtSearchAllCriteria } from '../models/logTSearchAllCriteria';
import { searchLogTsAllAction } from '../actions/logTActions';
import LoadingSpinner from './LoadingSpinner';
import '../styles/PrintButton.scss'; 
import { appInsights } from "../appInsights";
import { useAuth } from '../context/AuthContext';
const directionDescriptions: { [key: number]: string } = {
  0: 'In Down - In Downlink',
  1: 'Out Up - Out Uplink',
  2: 'In Up - In Uplink',
  3: 'Out Down - Out Downlink',
  4: 'In Gnd - In Ground',
  5: 'Out Gnd - Out Ground',
  6: 'Re Down - Reprocessed Downlink',
  7: 'Re Up - Reprocessed Uplink',
  8: 'Re Gnd - Reprocessed Ground'
};

interface PrintButtonProps {
  searchCriteria: LogtSearchAllCriteria;
  className?: string; // Add className as an optional prop
  searchExecuted: boolean;  // New prop to determine if search was executed
  user: any; // Add user prop
  onLoadingChange?: (loading: boolean) => void; // Callback for loading state changes
}

const PrintButton: React.FC<PrintButtonProps> = ({ searchCriteria, searchExecuted, className, user, onLoadingChange }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const updateLoading = (newLoading: boolean) => {
    setLoading(newLoading);
    onLoadingChange?.(newLoading);
  };

  // Determine if the button should be disabled
  const isDisabled = !searchExecuted || loading;
  const userName = `${user.given_name} ${user.family_name}`;
  const userEmail = user.email || "Unknown";
  const userGroups = useAuth();
  const accessToken = userGroups?.userData?.access_token || '';
  
  const handlePrint = async () => {
    if (isDisabled) return;
    appInsights.trackEvent({
      name: "PrintButtonClicked",
      properties: {
        timestamp: new Date().toISOString(),
        userName,
        userEmail,
        searchCriteria,
      },
    });
  
    updateLoading(true);
    try {
      const data = await dispatch<any>(searchLogTsAllAction(searchCriteria, accessToken));

      if (!Array.isArray(data)) {
        throw new Error('Expected data to be an array');
      }

      // Generate the print content
      const printContent = `
        <html>
          <head>
            <style>
              body {
                font-family: 'AS Circular', 'Helvetica Neue', Arial, sans-serif;;
                margin: 20px;
                color: #333;
              }
              h1 {
                text-align: center;
                font-size: 20px;
                margin-bottom: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                table-layout: fixed; /* Ensures columns have a fixed width */
              }
              table, th, td {
                border: 1px solid black;
              }
              th {
                background-color: #f2f2f2;
                text-align: left;
                font-size: 14px; /* Slightly smaller font for headers */
              }
              th, td {
                padding: 6px;
                text-align: left;
                font-size: 12px; /* Reduce font size to fit more text */
                word-wrap: break-word; /* Ensure long text wraps within the cell */
              }
              td.message {
                white-space: pre-wrap; /* Keep line breaks in message */
              }
            </style>
          </head>
          <body>
            <h1>ACARS History</h1>
            <table>
              <thead>
                <tr>
                  <th style="width: 15%;">Date & Time</th>
                  <th style="width: 12%;">Direction</th>
                  <th style="width: 11%;">Message Type</th>
                  <th style="width: 6%;">SMI</th>
                  <th style="width: 10%;">Medium</th>
                  <th style="width: 10%;">Aircraft</th>
                  <th style="width: 10%;">Flight#</th>
                  <th style="width: 8%;">ORIG</th>
                  <th style="width: 8%;">DEST</th>
                  <th style="width: 30%;">Message</th>
                </tr>
              </thead>
              <tbody>
                ${data.map((row: { logtTS: any; direction: any; messageType: any; smi: any; medium: any; aircraftId: any; flightId: any; departureAirportIATA: any; arrivalAirportIATA: any; logtContentMessage: any }) => `
                  <tr>
                    <td>${row.logtTS}</td>
                    <td>${directionDescriptions[row.direction] || row.direction}</td>
                    <td>${row.messageType}</td>
                    <td>${row.smi}</td>
                    <td>${row.medium}</td>
                    <td>${row.aircraftId}</td>
                    <td>${row.flightId}</td>
                    <td>${row.departureAirportIATA}</td>
                    <td>${row.arrivalAirportIATA}</td>
                    <td class="message">${row.logtContentMessage || 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;

      // Open the print window and print the content
      const printWindow = window.open('', '_blank');
      printWindow?.document.open();
      printWindow?.document.write(printContent);
      printWindow?.document.close();
      printWindow?.print();
    } catch (error) {
      console.error('Error fetching data for printing:', error);
    } finally {
      updateLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner size="large" />
      ) : (
        <button
          onClick={handlePrint}
          className={className ? className : 'print-button'}
          aria-label="Print"
          disabled={isDisabled}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M19 8H5a2 2 0 0 0-2 2v6h4v4h10v-4h4v-6a2 2 0 0 0-2-2zm-3 11H8v-4h8v4zm2-10V4H6v5h12zm-3-3v3H9V6h6z" />
          </svg>
        </button>
      )}
    </>
  );
};

export default PrintButton;
